import React, { useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { Alert, Autocomplete, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useGetAudiencePublicList, useSearchAudienceAdminList } from '~/hooks/apis/promo/useAudiences';
import useCurrentBrand from '~/hooks/useCurrentBrand';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { AudiencePublicMatchOutput, AudienceSearchOutput } from '~/services/PromoService';

const SEARCH_DEBOUNCE_MS = 250;

type Props = {
  defaultAudienceId?: string;
};

function audienceLabel(audience?: Pick<AudienceSearchOutput, 'audienceId' | 'audienceName'> | null) {
  if (!audience) {
    return '';
  }
  return `${audience.audienceName} (${audience.audienceId})`;
}

function formatValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : '—';
  }
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    return entries.length > 0
      ? entries
          .map(([key, item]) => {
            const formattedItem = formatValue(item);
            return `${key}: ${formattedItem}`;
          })
          .join(', ')
      : '—';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return `${value}`;
  }
  return '—';
}

function renderAudienceDetails(audience: AudiencePublicMatchOutput | AudienceSearchOutput) {
  return Object.entries(audience).map(([key, value]) => (
    <Box key={key} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ minWidth: 140, fontWeight: 600 }}>
        {key}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {formatValue(value)}
      </Typography>
    </Box>
  ));
}

export default function AudienceTesterWidget({ defaultAudienceId = '' }: Props) {
  const brand = useCurrentBrand();
  const { user } = useCurrentUser();
  const currentUserId = user?.id_member?.trim() || user?.memberId?.trim() || '';

  const [userId, setUserId] = useState(currentUserId);
  const [audienceInput, setAudienceInput] = useState(defaultAudienceId);
  const [debouncedAudienceInput, setDebouncedAudienceInput] = useState(defaultAudienceId);
  const [selectedAudience, setSelectedAudience] = useState<AudienceSearchOutput | null>(null);
  const [submittedFilters, setSubmittedFilters] = useState<{ userId: string; audienceId?: string } | null>(null);

  const debouncedSetAudienceInput = useMemo(
    () => debounce((value: string) => setDebouncedAudienceInput(value), SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(
    () => () => {
      debouncedSetAudienceInput.cancel();
    },
    [debouncedSetAudienceInput],
  );

  useEffect(() => {
    if (!userId && currentUserId) {
      setUserId(currentUserId);
    }
  }, [currentUserId, userId]);

  useEffect(() => {
    if (defaultAudienceId) {
      setAudienceInput(defaultAudienceId);
      setDebouncedAudienceInput(defaultAudienceId);
      setSubmittedFilters({ userId: currentUserId, audienceId: defaultAudienceId });
    }
  }, [currentUserId, defaultAudienceId]);

  const searchResultsQuery = useSearchAudienceAdminList({
    query: {
      brand,
      query: debouncedAudienceInput.trim() || undefined,
      limit: 20,
    },
    enabled: debouncedAudienceInput.trim().length > 0,
  });

  const audienceOptions = searchResultsQuery.data?.result.audienceList ?? [];

  const publicAudienceQuery = useGetAudiencePublicList({
    query: {
      brand,
      userId: submittedFilters?.userId || undefined,
      audienceId: submittedFilters?.audienceId || undefined,
    },
    enabled: Boolean(submittedFilters?.userId || submittedFilters?.audienceId),
  });

  const publicAudiences = publicAudienceQuery.data?.result.audienceList ?? [];

  const noOptionsText = searchResultsQuery.isFetching
    ? 'Searching...'
    : audienceInput.trim()
      ? 'No audiences found'
      : 'Start typing an audience ID or name';

  const handleSearch = () => {
    const nextAudienceId = selectedAudience?.audienceId || audienceInput.trim() || undefined;
    setSubmittedFilters({
      userId: userId.trim(),
      audienceId: nextAudienceId,
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <PageSubheader title="Audience tester" />
        <Typography variant="body2" color="text.secondary">
          Look up public audience matches for a specific user, optionally narrowing to one audience.
        </Typography>

        <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
          <TextField label="User ID" value={userId} onChange={(event) => setUserId(event.target.value)} fullWidth />
          <Autocomplete
            fullWidth
            freeSolo
            options={audienceOptions}
            value={selectedAudience}
            inputValue={audienceInput}
            loading={searchResultsQuery.isFetching}
            getOptionLabel={(option) => (typeof option === 'string' ? option : audienceLabel(option))}
            filterOptions={(options) => options}
            isOptionEqualToValue={(option, value) => option.audienceId === value.audienceId}
            onInputChange={(_, value) => {
              setAudienceInput(value);
              debouncedSetAudienceInput(value);
            }}
            onChange={(_, value) => {
              if (typeof value === 'string') {
                setSelectedAudience(null);
                setAudienceInput(value);
                setDebouncedAudienceInput(value);
                return;
              }
              setSelectedAudience(value);
              setAudienceInput(value?.audienceId ?? '');
              setDebouncedAudienceInput(value?.audienceId ?? '');
            }}
            noOptionsText={noOptionsText}
            renderInput={(params) => <TextField {...params} label="Audience ID" helperText="Type to search" />}
          />
        </Stack>

        <Box>
          <Button variant="contained" onClick={handleSearch} disabled={!userId.trim()}>
            Test audience
          </Button>
        </Box>

        {publicAudienceQuery.isError ? (
          <Alert severity="error">
            {publicAudienceQuery.error instanceof Error
              ? publicAudienceQuery.error.message
              : 'Failed to load audiences'}
          </Alert>
        ) : null}

        {publicAudienceQuery.isLoading ? <Typography variant="body2">Loading audience matches...</Typography> : null}

        {!publicAudienceQuery.isLoading && !publicAudienceQuery.isError && submittedFilters ? (
          publicAudiences.length > 0 ? (
            <Stack spacing={2}>
              <Typography variant="subtitle2">Matched audiences</Typography>
              {publicAudiences.map((audience) => (
                <Paper key={audience.audienceId} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">
                      {audience.audienceName}{' '}
                      <Typography component="span" color="text.secondary">
                        ({audience.audienceId})
                      </Typography>
                    </Typography>
                    {renderAudienceDetails(audience)}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Alert severity="info">No audiences returned for the selected user.</Alert>
          )
        ) : null}
      </Stack>
    </Paper>
  );
}
